import asyncEachSeries from 'async/eachSeries';
import EventEmitter from 'events';
import { ethers } from 'ethers';

import CoinStore from './coin_store';
import SwapStore from './swap_store';
import WalletStore from './wallet_store';

import Api from '../tools/api';
import { isSameAddress } from '../tools/eth_tools';
import Storage from '../tools/storage';
import SwapTools from '../tools/swap_tools';
import util from '../tools/util';
const { BigNumber } = ethers;

export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  isLoaded,
  getList,
  fetch,
};

const SWAP_STATUS_KEY = 'SWAP_ORDER_STATUS_KEY';

//const APPROVE_VALUE = ethers.BigNumber.from(2).pow(255);
const RATIO = BigNumber.from(10).pow(38).mul(3);

const g_orderState = new Map<string, any>();
const g_marketState = new Map<string, any>();
let g_isLoaded = false;
let g_orderList = [];

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function init(done) {
  _loadOrderState();
  done && done();
}
function isLoaded() {
  return g_isLoaded;
}
function getList() {
  return g_orderList;
}
function fetch() {
  const wallet_addr = WalletStore.getAddress();
  if (wallet_addr) {
    const opts = {
      url: '/cached/1/harbour_swap/sender/' + wallet_addr,
    };
    Api.cdnGet(opts, (err, body) => {
      if (err) {
        util.errorLog('OrderHistoryStore.fetch: err:', err, body);
      } else {
        body.order_list.forEach(_fixupOrder);
        g_orderList = _markListDups(body.order_list);
        g_isLoaded = true;
        _checkOrderList();
        _emit('load');
      }
    });
  } else {
    g_orderList = [];
    g_isLoaded = true;
    _emit('load');
  }
}
function _fixupOrder(order) {
  order.buy_quantity = BigNumber.from(order.buy_quantity);
  order.sell_quantity = BigNumber.from(order.sell_quantity);
  order.price_ratio = BigNumber.from(order.price_ratio);
  const {
    chain_id,
    token_addr,
    currency_addr,
    price_ratio,
    buy_quantity,
    sell_quantity,
  } = order;
  order.token = CoinStore.getCoinByAddress(chain_id, token_addr);
  order.currency = CoinStore.getCoinByAddress(chain_id, currency_addr);

  const token_wei = CoinStore.weiAmount(1, order.token);
  const price_wei = price_ratio.mul(token_wei).div(RATIO);
  order.price = CoinStore.coinAmount(price_wei, order.currency);
  order.is_sell = false;
  if (buy_quantity.gt(0)) {
    order.order_quantity = CoinStore.coinAmount(buy_quantity, order.token);
  } else if (sell_quantity.gt(0)) {
    order.is_sell = true;
    order.order_quantity = CoinStore.coinAmount(sell_quantity, order.token);
  } else if (buy_quantity.lt(0)) {
    order.is_cancel = true;
    order.order_quantity = CoinStore.coinAmount(
      buy_quantity.mul(-1),
      order.token
    );
  } else if (sell_quantity.lt(0)) {
    order.is_cancel = true;
    order.is_sell = true;
    order.order_quantity = CoinStore.coinAmount(
      sell_quantity.mul(-1),
      order.token
    );
  }
  _fixupOrderState(order);
  return order;
}
function _fixupOrderState(order) {
  const key = _orderKey(order);
  const state = g_orderState.get(key);
  if (state) {
    Object.assign(order, state);
  }
  const market_key = _marketKey(order);
  const market = g_marketState.get(market_key);
  if (market) {
    order.settle_direction = market.settle_direction;
  } else {
    order.settle_direction = '';
  }
  return order;
}
function _markListDups(list) {
  const dup_map = {};
  list.forEach((order) => {
    const dup_key = _orderDupKey(order);
    if (dup_key in dup_map) {
      const key = _orderKey(order);
      order.is_done = true;
      g_orderState.set(key, { is_done: true });
    } else {
      dup_map[dup_key] = true;
    }
  });
  _saveOrderState();
  return list;
}
function _refreshOrderList() {
  const new_list = g_orderList.map((order) =>
    Object.assign({}, _fixupOrderState(order))
  );
  g_orderList = _markListDups(new_list);
}

let g_isOrderCheckRunning = false;
function _checkOrderList() {
  if (!g_isOrderCheckRunning) {
    g_isOrderCheckRunning = true;

    let fetch_count = 0;
    asyncEachSeries(
      g_orderList,
      (order, done) => {
        const key = _orderKey(order);
        const old_state = g_orderState.get(key);
        if (old_state && old_state.is_done) {
          done();
        } else if (order.is_cancel) {
          done();
        } else {
          fetch_count++;
          _fetchOrderState(order, done);
        }
      },
      () => {
        g_isOrderCheckRunning = false;
        if (fetch_count > 0) {
          _refreshOrderList();
          _checkMarketList();
          _emit('update');
        }
      }
    );
  }
}
function _fetchOrderState(order, done) {
  const key = _orderKey(order);
  const { chain_id, is_sell } = order;
  const opts = {
    chain_id,
    contract_addr: SwapStore.getSwapContract(chain_id),
    token_addr: order.token_addr,
    currency_addr: order.currency_addr,
    price_ratio: order.price_ratio,
    index: order.order_index,
  };
  const func = is_sell ? SwapTools.getSellOrder : SwapTools.getBuyOrder;
  func(opts, (err, result) => {
    let new_state = null;
    if (err && err.code === 'CALL_EXCEPTION') {
      new_state = { is_done: true };
    } else if (!err && result) {
      const user = is_sell ? result.seller : result.buyer;
      if (isSameAddress(user, WalletStore.getAddress())) {
        if (result.quantity.lte(0)) {
          new_state = { is_done: true };
        } else {
          new_state = {
            quantity_wei: result.quantity,
            order_quantity: CoinStore.coinAmount(result.quantity, order.token),
            is_done: false,
          };
        }
      } else {
        new_state = { is_done: true };
      }
    }
    if (new_state) {
      g_orderState.set(key, new_state);
      _saveOrderState();
    }
    done();
  });
}
let g_isMarketCheckRunning = false;
function _checkMarketList() {
  if (!g_isMarketCheckRunning) {
    g_isMarketCheckRunning = true;
    const market_map = {};
    g_orderList.forEach((order) => {
      if (!order.is_cancel && !order.is_done) {
        const key = _marketKey(order);
        market_map[key] = {
          chain_id: order.chain_id,
          token_addr: order.token_addr,
          currency_addr: order.currency_addr,
          price_ratio: order.price_ratio,
        };
      }
    });
    const check_list = Object.values(market_map);
    asyncEachSeries(
      check_list,
      (market, done) => {
        const key = _marketKey(market);
        const opts = {
          contract_addr: SwapStore.getSwapContract(market.chain_id),
          ...market,
        };
        SwapTools.fetchMarket(opts, (err, data) => {
          if (!err && data) {
            g_marketState.set(key, data);
          }
          done();
        });
      },
      () => {
        g_isMarketCheckRunning = false;
        _refreshOrderList();
        _emit('update');
      }
    );
  }
}

function noop() {}
function _loadOrderState(done?: (err?: any) => void) {
  Storage.getItem(SWAP_STATUS_KEY, (err, data) => {
    if (!err && data && typeof data === 'object') {
      for (let key in data) {
        g_orderState.set(key, data[key]);
      }
    }
    done && done();
  });
}
function _saveOrderState(done?: (err?: any) => void) {
  const data = {};
  g_orderState.forEach((value, key) => (data[key] = value));
  Storage.setItem(SWAP_STATUS_KEY, data, done || noop);
}
function _orderDupKey(order) {
  return (
    order.token_addr +
    '_' +
    order.currency_addr +
    '_' +
    String(order.price_ratio) +
    '_' +
    order.order_index +
    '_' +
    order.is_sell
  ).toLowerCase();
}
function _orderKey(order) {
  return (_orderDupKey(order) + '_' + order.created_time).toLowerCase();
}
function _marketKey(order) {
  return (
    order.token_addr +
    '_' +
    order.currency_addr +
    '_' +
    String(order.price_ratio)
  ).toLowerCase();
}
